import { type ReactNode, useMemo } from 'react'

import { ErrorBlock } from '@/components/common/ErrorBlock'
import { LoadingMask } from '@/components/common/LoadingMask/LoadingMask'
import { useIsNavigating } from '@/components/providers/NavigationProvider'
import { ScrollspyProvider } from '@/components/providers/ScrollspyProvider'
import { type GalleryViewSlugType } from '@/defs/gallery'

import { GalleryControls } from '../GalleryControls/GalleryControls'
import { useGalleryDisplay } from '../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryGroups } from '../GalleryGroupsProvider/GalleryGroupsProvider'
import { GalleryViewRecs } from '../GalleryViewRecs/GalleryViewRecs'
import { GalleryViewTable } from '../GalleryViewTable/GalleryViewTable'
import { GalleryViewText } from '../GalleryViewText/GalleryViewText'

import styles from './GalleryLayout.module.scss'

const CONFIG: Record<GalleryViewSlugType, (props: Record<string, unknown>) => ReactNode> = {
  cards: (props) => <GalleryViewRecs variant='images' {...props} />,
  stats: (props) => <GalleryViewRecs {...props} />,
  table: (props) => <GalleryViewTable {...props} />,
  text: (props) => <GalleryViewText {...props} />,
}

export type GalleryLayoutProps = {
  galleryViewProps: {
    openLinksInNewTab?: boolean
  }
}

export const GalleryLayout = ({ galleryViewProps }: GalleryLayoutProps) => {
  const isNavigating = useIsNavigating()
  const { view } = useGalleryDisplay()
  const { groups, hasError, loading: loadingRecs } = useGalleryGroups()
  const slugs = useMemo(() => groups.map((g) => g.slug), [groups])

  const loading = isNavigating || loadingRecs

  if (hasError) return <ErrorBlock />

  return (
    <section className={styles.container}>
      <ScrollspyProvider slugs={slugs}>
        <GalleryControls />
        {CONFIG[view](galleryViewProps)}
        <LoadingMask loading={loading} zIndex='calc(var(--z-header-gallery) + 3)' />
      </ScrollspyProvider>
    </section>
  )
}
